/**
 * Realistic job titles for the /demo lead inbox, picked by trade.
 *
 * The fixture leads (`fixtures/leads.ts`) are written as plumbing calls —
 * names, messages, urgency, the works. When the dashboard is opened from an
 * invite for a different trade, the inbox should not still say "Kitchen drain
 * cleaning" under a roofer's logo. Rather than write eight separate lead sets,
 * each lead keeps its name/message/status and only its `service` title is
 * swapped for one drawn from this map, in order, wrapping if there are more
 * leads than jobs for that trade.
 */

export type TradeKey =
  | 'plumbing'
  | 'hvac'
  | 'roofing'
  | 'electrical'
  | 'landscaping'
  | 'cleaning'
  | 'generic';

const TRADE_JOBS: Record<TradeKey, string[]> = {
  plumbing: [
    'Emergency repair',
    'Water heater replacement',
    'Kitchen drain cleaning',
    'Bathroom remodel rough-in',
    'Sump pump replacement',
    'Outdoor spigot repair',
    'Garbage disposal install',
    'Whole-house repipe',
  ],
  hvac: [
    'No heat call',
    'AC not cooling',
    'Furnace tune-up',
    'Ductless mini-split install',
    'Thermostat replacement',
    'Annual maintenance plan',
    'Duct cleaning',
  ],
  roofing: [
    'Storm damage inspection',
    'Roof leak repair',
    'Full roof replacement',
    'Gutter installation',
    'Shingle repair',
    'Insurance claim estimate',
    'Skylight install',
  ],
  electrical: [
    'Panel upgrade',
    'Outlet not working',
    'EV charger install',
    'Lighting installation',
    'Whole-house rewire',
    'Generator hookup',
    'Code violation fix',
  ],
  landscaping: [
    'Spring cleanup',
    'Lawn maintenance plan',
    'Irrigation repair',
    'Tree removal',
    'Sod installation',
    'Mulching and edging',
    'Patio / hardscape quote',
  ],
  cleaning: [
    'One-time deep clean',
    'Recurring biweekly clean',
    'Move-out clean',
    'Post-construction clean',
    'Window cleaning',
    'Carpet shampoo',
  ],
  generic: ['Free estimate request', 'Service call', 'Follow-up quote', 'General inquiry'],
};

/** Trade string from an invite ("Plumbing", "HVAC / Heating", …) → one of our keys. */
export function normalizeTradeKey(trade?: string | null): TradeKey {
  const value = (trade || '').toLowerCase();
  if (value.includes('plumb')) return 'plumbing';
  if (value.includes('hvac') || value.includes('heat') || value.includes('cool') || value.includes('furnace'))
    return 'hvac';
  if (value.includes('roof')) return 'roofing';
  if (value.includes('electric')) return 'electrical';
  if (value.includes('landscap') || value.includes('lawn')) return 'landscaping';
  if (value.includes('clean') || value.includes('maid') || value.includes('janitor')) return 'cleaning';
  return 'generic';
}

export function jobsForTrade(trade?: string | null): string[] {
  return TRADE_JOBS[normalizeTradeKey(trade)];
}
